import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { customerApi } from '../lib/api';

export const Route = createFileRoute('/customers/new')({
  component: NewCustomerPage,
});

const COUNTRIES = [
  { code: 'JP', name: '日本', flag: '🇯🇵' },
  { code: 'US', name: '美国', flag: '🇺🇸' },
  { code: 'GB', name: '英国', flag: '🇬🇧' },
  { code: 'DE', name: '德国', flag: '🇩🇪' },
  { code: 'FR', name: '法国', flag: '🇫🇷' },
  { code: 'KR', name: '韩国', flag: '🇰🇷' },
  { code: 'CN', name: '中国', flag: '🇨🇳' },
];

const SOURCES = [
  { value: 'manual', label: '手动录入' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'jimoto', label: 'jimoto ツクリ' },
  { value: 'thomasnet', label: 'ThomasNet' },
  { value: 'kompass', label: 'Kompass' },
  { value: 'exhibition', label: '展会' },
  { value: 'referral', label: '客户介绍' },
  { value: 'customs', label: '海关数据' },
  { value: 'google', label: 'Google Dork' },
];

const STAGES = [
  { value: 'prospect', label: '潜在' },
  { value: 'contacted', label: '已联系' },
  { value: 'quoted', label: '已报价' },
  { value: 'sampled', label: '已样品' },
  { value: 'ordered', label: '已大货' },
  { value: 'delivered', label: '已交付' },
  { value: 'lost', label: '流失' },
];

function NewCustomerPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    name: '',
    country: 'US',
    city: '',
    website: '',
    source: 'manual',
    stage: 'prospect',
    incotermPref: 'FOB',
    paymentPref: 'T/T 30/70',
    currencyPref: 'USD',
    tags: '',
    notes: '',
  });
  const [error, setError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: () =>
      customerApi.create({
        name: form.name,
        country: form.country,
        city: form.city || undefined,
        website: form.website || undefined,
        source: form.source,
        stage: form.stage as
          | 'prospect'
          | 'contacted'
          | 'quoted'
          | 'sampled'
          | 'ordered'
          | 'delivered'
          | 'lost',
        incotermPref: form.incotermPref,
        paymentPref: form.paymentPref,
        currencyPref: form.currencyPref,
        tags: form.tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
        notes: form.notes || undefined,
      }),
    onSuccess: (customer) => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      navigate({ to: '/customers/$customerId', params: { customerId: customer.id } });
    },
    onError: (e: Error) => {
      setError(e.message);
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    createMutation.mutate();
  }

  return (
    <div className="max-w-2xl">
      <Link to="/customers" className="text-sm text-accent inline-block mb-3">
        ← 返回客户列表
      </Link>

      <h1 className="text-2xl font-semibold mb-5">新建客户</h1>

      <form
        onSubmit={handleSubmit}
        className="bg-surface border border-line rounded p-6 flex flex-col gap-4"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="flex flex-col gap-1 text-xs text-ink-2">
            公司名 *
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="px-3 py-2 border border-line rounded text-sm"
              placeholder="例: Acme Toy Co."
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-ink-2">
            国家 *
            <select
              value={form.country}
              onChange={(e) => setForm({ ...form, country: e.target.value })}
              className="px-3 py-2 border border-line rounded text-sm"
            >
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.flag} {c.name} ({c.code})
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="flex flex-col gap-1 text-xs text-ink-2">
            城市
            <input
              type="text"
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              className="px-3 py-2 border border-line rounded text-sm"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-ink-2">
            官网
            <input
              type="url"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              className="px-3 py-2 border border-line rounded text-sm"
              placeholder="https://..."
            />
          </label>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="flex flex-col gap-1 text-xs text-ink-2">
            来源
            <select
              value={form.source}
              onChange={(e) => setForm({ ...form, source: e.target.value })}
              className="px-3 py-2 border border-line rounded text-sm"
            >
              {SOURCES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-ink-2">
            阶段
            <select
              value={form.stage}
              onChange={(e) => setForm({ ...form, stage: e.target.value })}
              className="px-3 py-2 border border-line rounded text-sm"
            >
              {STAGES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <label className="flex flex-col gap-1 text-xs text-ink-2">
            偏好贸易形式
            <select
              value={form.incotermPref}
              onChange={(e) => setForm({ ...form, incotermPref: e.target.value })}
              className="px-3 py-2 border border-line rounded text-sm"
            >
              {['EXW', 'FOB', 'CFR', 'CIF', 'DAP', 'DDP'].map((i) => (
                <option key={i} value={i}>
                  {i}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-ink-2">
            付款方式
            <select
              value={form.paymentPref}
              onChange={(e) => setForm({ ...form, paymentPref: e.target.value })}
              className="px-3 py-2 border border-line rounded text-sm"
            >
              {['T/T 30/70', 'T/T 50/50', 'L/C at sight', 'O/A 30', 'PayPal'].map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-ink-2">
            币种
            <select
              value={form.currencyPref}
              onChange={(e) => setForm({ ...form, currencyPref: e.target.value })}
              className="px-3 py-2 border border-line rounded text-sm"
            >
              {['USD', 'JPY', 'EUR', 'CNY', 'HKD'].map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="flex flex-col gap-1 text-xs text-ink-2">
          标签（英文逗号分隔）
          <input
            type="text"
            value={form.tags}
            onChange={(e) => setForm({ ...form, tags: e.target.value })}
            className="px-3 py-2 border border-line rounded text-sm"
            placeholder="玩具, VIP, 复购"
          />
        </label>

        <label className="flex flex-col gap-1 text-xs text-ink-2">
          备注
          <textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={3}
            className="px-3 py-2 border border-line rounded text-sm"
            placeholder="客户背景 / 决策人 / 跟进记录..."
          />
        </label>

        {error && (
          <div className="px-3 py-2 bg-bad/10 border border-bad rounded text-xs text-bad">
            {error}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
          <Link
            to="/customers"
            className="px-4 py-2 border border-line rounded text-sm hover:bg-surface-2"
          >
            取消
          </Link>
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="px-4 py-2 bg-accent text-white rounded text-sm font-medium hover:bg-accent-2 disabled:opacity-50"
          >
            {createMutation.isPending ? '创建中...' : '创建客户'}
          </button>
        </div>
      </form>
    </div>
  );
}
